import {
  Box,
  Modal,
  Typography,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton,
  useMediaQuery
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { FormikProvider, useFormik, Form } from 'formik';
import { GetDepartmentList } from '../../../network/Department';
import { useAuth } from '../../customContextProviders/AuthContext';
import { GetRoleList } from '../../../network/Role';
import { AddUserData, UpdateUserProfile } from '../../../network/User';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', md: '40%' },
  maxWidth: { xs: '70%', md: '40%' },
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

export default function AddNewUserModal({
  openAssignedModal,
  handleClose,
  userData,
  organizationList,
  setAlertMetas,
  alertMetas,
  onAction
}) {
  const classes = useStyles();
  const [roleList, setRoleList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const { sessionUserData } = useAuth();

  const userRole = sessionUserData?.user?.userRole?.roleName;
  const orgId = sessionUserData?.user?.organization?.id;

  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const userSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
      .required('Required'),
    password: userData?.id ? Yup.string().notRequired() : Yup.string().required('Required'),
    organizationId:
      userRole.toUpperCase() === 'SUPERADMIN'
        ? Yup.string().required('Required')
        : Yup.string().notRequired(),
    roleId: Yup.string().required('Required')
  });

  const addNewUserFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: userData?.email || '',
      name: userData?.name || '',
      phone: userData?.phone || '',
      password: '',
      organizationId:
        userData?.organizationId || userRole?.toUpperCase() !== 'SUPERADMIN' ? orgId : '',
      role: 'admin',
      roleId: userData?.userRole?.id || '',
      departmentId: userData?.department?.id || userRole.toUpperCase() !== 'SUPERADMIN' ? '' : ''
    },
    validationSchema: userSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = { ...values };

      if (!payload.departmentId) {
        delete payload.departmentId;
      }

      createUser(payload);
      resetForm();
    }
  });

  const { errors, touched, handleSubmit, getFieldProps, setFieldValue } = addNewUserFormik;

  const getAllRole = async () => {
    try {
      const res = await GetRoleList(orgId);
      if (res) {
        setRoleList(res?.data?.data);
      }
    } catch (error) {
      console.log(error?.response?.data?.message);
    }
  };

  const getAllDepartment = async () => {
    try {
      const res = await GetDepartmentList();
      setDepartmentList(res?.data?.data);
    } catch (error) {
      console.log(error?.response?.data?.message);
    }
  };

  const createUser = async (values) => {
    const payload = { ...values };

    if (userData?.id) {
      delete payload.email;
      delete payload.password;
    }
    try {
      const res = userData?.id
        ? await UpdateUserProfile(userData?.id, payload)
        : await AddUserData(payload);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: userData?.id ? 'User Updated Successfully!' : 'User Created Successfully!'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        handleClose();
        onAction();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  useEffect(() => {
    getAllRole();
    getAllDepartment();
  }, []);

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="add-user-modal"
      >
        <FormikProvider value={addNewUserFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 900, mb: 3 }}>
                {userData?.id ? 'Update User' : 'Create New User'}
              </Typography>
              <Box display="flex" flexDirection="column" gap="10px">
                <FormControl data-testid="name-form-control" fullWidth>
                  <TextField
                    label={
                      <Typography
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '14px'
                          },
                          fontWeight: 500
                        }}
                      >
                        Name *
                      </Typography>
                    }
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="name"
                    name="name"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </FormControl>
                <FormControl data-testid="user-email-formControl" fullWidth>
                  <TextField
                    disabled={userData?.id}
                    label={
                      <Typography
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '14px'
                          },
                          fontWeight: 500
                        }}
                      >
                        Email *
                      </Typography>
                    }
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="email"
                    name="email"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </FormControl>
                {!userData?.id && (
                  <FormControl fullWidth>
                    <TextField
                      data-testid="user-password-formControl"
                      label={
                        <Typography
                          sx={{
                            fontSize: {
                              xs: '12px',
                              sm: '14px'
                            },
                            fontWeight: 500
                          }}
                        >
                          Password *
                        </Typography>
                      }
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '14px 8px', sm: '16.5px' }
                        }
                      }}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('password')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              <Icon
                                style={{ fontSize: isSmallScreen ? '18px' : '24px' }}
                                icon={showPassword ? eyeFill : eyeOffFill}
                              />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      error={Boolean(touched.password && errors.password)}
                      helperText={touched.password && errors.password}
                    />
                  </FormControl>
                )}
                <FormControl data-testid="user-phone-formControl" fullWidth>
                  <TextField
                    label="Mobile Number *"
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '12px', sm: '14px' },
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="phone"
                    name="phone"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('phone')}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </FormControl>
                {userRole.toUpperCase() === 'SUPERADMIN' && (
                  <FormControl
                    fullWidth
                    error={Boolean(touched.organizationId && errors.organizationId)}
                  >
                    <InputLabel
                      sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                      id="organizationId-label"
                    >
                      Select Organization *
                    </InputLabel>
                    <Select
                      sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                      labelId="organizationId-label"
                      label="Select Organization *"
                      id="organizationId"
                      IconComponent={ExpandMoreIcon}
                      name="organizationId"
                      {...getFieldProps('organizationId')}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 180,
                            overflowY: 'auto'
                          }
                        }
                      }}
                    >
                      {organizationList.map((item) => (
                        <MenuItem className={classes.menuItem} value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.organizationId && errors.organizationId && (
                      <FormHelperText>{errors.organizationId}</FormHelperText>
                    )}
                  </FormControl>
                )}
                <FormControl
                  data-testid="select-role-formControl"
                  fullWidth
                  error={Boolean(touched.roleId && errors.roleId)}
                >
                  <InputLabel
                    sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                    id="roleId-label"
                  >
                    Select Role *
                  </InputLabel>
                  <Select
                    sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                    labelId="roleId-label"
                    label="Select Role *"
                    id="roleId"
                    IconComponent={ExpandMoreIcon}
                    name="roleId"
                    {...getFieldProps('roleId')}
                    onChange={(e) => {
                      const selectedRole = roleList.find((role) => role.id === e.target.value);
                      setFieldValue('roleId', selectedRole.id);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 150,
                          overflowY: 'auto'
                        }
                      }
                    }}
                  >
                    {roleList.map((item) => (
                      <MenuItem className={classes.menuItem} value={item.id} key={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.roleId && errors.roleId && (
                    <FormHelperText>{errors.roleId}</FormHelperText>
                  )}
                </FormControl>

                {userRole.toUpperCase() !== 'SUPERADMIN' && (
                  <FormControl
                    data-testid="select-department-formControl"
                    fullWidth
                    error={Boolean(touched.roleId && errors.roleId)}
                    sx={{ mb: 2 }}
                  >
                    <InputLabel
                      sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                      id="roleId-label"
                    >
                      Select Department *
                    </InputLabel>
                    <Select
                      sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                      labelId="departmentId-label"
                      label="Select Department *"
                      id="departmentId"
                      IconComponent={ExpandMoreIcon}
                      name="departmentId"
                      {...getFieldProps('departmentId')}
                      onChange={(e) => {
                        const selectedDepartment = departmentList.find(
                          (department) => department.id === e.target.value
                        );
                        setFieldValue('departmentId', selectedDepartment.id);
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 150,
                            overflowY: 'auto'
                          }
                        }
                      }}
                    >
                      {departmentList.map((item) => (
                        <MenuItem className={classes.menuItem} value={item?.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.departmentId && errors.departmentId && (
                      <FormHelperText>{errors.departmentId}</FormHelperText>
                    )}
                  </FormControl>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap'
                  }}
                >
                  <Button variant="outlined" size="small" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button type="submit" size="small" variant="contained">
                    {userData?.id ? 'Save Changes' : 'Create User'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>
    </Box>
  );
}
